import { cloneElement, Fragment, useMemo, useState } from 'react';
import styled from 'styled-components';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Dialog from '@mui/material/Dialog';
import Step from '@mui/material/Step';
import StepContent from '@mui/material/StepContent';
import Stepper from '@mui/material/Stepper';
import Typography from '@mui/material/Typography';

import { DialogHeader } from '../dialog/DialogHeader';
import {
  DialogContentContainer,
  DialogMainContent,
} from '../dialog/DialogStyledComponents';

import {
  WorkflowPhaseDto,
  WorkflowPhaseSectionDto,
} from '@act/shared/data-transfer-objects';
import { StepButton } from '@mui/material';
import {
  ProgressContainerWithSpinner,
  SECTION_TYPES,
  WorkflowButtonGroup,
} from 'react-components/workflows/WorkflowDialogSharedConstants';
import { legacySurveyRequests } from 'redux/requests/legacy-survey.requests';
import { workflowsRequests } from 'redux/requests/workflows.request';
import { completeAssignedWorkflowPhase } from 'redux/slices/workflowSlice.slice';
import { useAppDispatch } from 'redux/storeHooks';
import { useApiCall } from 'redux/utils/apiHook.hook';
import Survey from '../survey/Survey';
import { HTMLWrapper } from './SectionStyledComponents';

const PhaseDescriptionContainer = styled.div`
  margin: 16px 0;
`;

const ConstantWidthStepContent = styled(StepContent)`
  width: 500px;
`;

interface WorkflowCompletionProps {
  open: boolean;
  assignedPhaseId: number;
  phase: WorkflowPhaseDto;
  responseId: string;
  handleClose: (workflowFinished?: boolean) => void;
}

const WorkflowCompletionDialog = ({
  open,
  assignedPhaseId,
  phase,
  responseId,
  handleClose,
}: WorkflowCompletionProps) => {
  const [activeStep, setActiveStep] = useState<number>(0);
  const [dialogSections, setDialogSections] = useState<
    WorkflowPhaseSectionDto[]
  >([]);
  const [isSurveyValid, setIsSurveyValid] = useState<boolean>(false);

  const [sections, isLoading, getSectionsByPhaseId] = useApiCall(
    workflowsRequests.getSectionsByPhaseId
  );

  const [response, isResponseLoading, getResponseById] = useApiCall(
    legacySurveyRequests.getResponse
  );

  const [previewSummary, isSummaryLoading, getNextPhasePreviewSummary] =
    useApiCall(workflowsRequests.getNextPhasePreviewSummary);

  const dispatch = useAppDispatch();

  useMemo(() => {
    if (phase) {
      getSectionsByPhaseId({
        params: { phaseId: phase.id, attachContent: 'true' },
      });
    }
  }, [phase]);

  useMemo(() => {
    if (sections?.length) {
      setDialogSections(
        sections.filter(
          section =>
            section.usableIn === 'workflow' || section.usableIn === 'both'
        )
      );
    }
  }, [sections]);

  useMemo(() => {
    getResponseById({ params: { id: responseId } });
  }, []);

  useMemo(() => {
    if (dialogSections?.length && response?.startedAt) {
      setActiveStep(dialogSections?.length);
    }
  }, [dialogSections, response]);

  useMemo(() => {
    if (activeStep === dialogSections?.length + 1) {
      getNextPhasePreviewSummary({ params: { phaseId: assignedPhaseId } });
    }
  }, [activeStep]);

  function handleValidityUpdate(isValid: boolean) {
    setIsSurveyValid(isValid);
  }

  function handleNext() {
    setActiveStep(prev => prev + 1);
  }

  function handleBack() {
    setActiveStep(prev => prev - 1);
  }

  function handleJump(index) {
    setActiveStep(index);
  }

  async function handleCompleteWorkflowPhase() {
    await dispatch(
      completeAssignedWorkflowPhase({
        params: null,
        body: { id: assignedPhaseId },
      })
    );
    handleClose(true);
  }

  return (
    <Dialog
      maxWidth="xl"
      open={open}
      onClose={() => handleClose(false)}
      role="dialog"
      aria-label="Workflow phase completion dialog"
    >
      <DialogContentContainer>
        <DialogHeader
          prefixAction={{
            title: 'return',
            callback: () => handleClose(false),
            icon: <ArrowBackIcon />,
          }}
        >
          {phase?.title}
        </DialogHeader>
        <DialogMainContent>
          <PhaseDescriptionContainer>
            <Typography variant="bodyLarge">{phase?.description}</Typography>
          </PhaseDescriptionContainer>
          {isLoading && <ProgressContainerWithSpinner />}
          {!isLoading && (
            <Stepper nonLinear activeStep={activeStep} orientation="vertical">
              {dialogSections.map((section, index) => (
                <Step key={section.title}>
                  <StepButton onClick={() => handleJump(index)}>
                    <Typography variant="titleMedium">
                      {section.title}
                    </Typography>
                  </StepButton>
                  <ConstantWidthStepContent>
                    <Fragment>
                      {cloneElement(SECTION_TYPES[section.type], {
                        section,
                        isStep: true,
                      })}
                    </Fragment>
                    <WorkflowButtonGroup
                      handleBack={handleBack}
                      handleNext={handleNext}
                      disableBack={index === 0}
                    />
                  </ConstantWidthStepContent>
                </Step>
              ))}
              <Step>
                <StepButton
                  onClick={() => handleJump(dialogSections.length + 0)}
                >
                  <Typography variant="titleMedium">Survey</Typography>
                </StepButton>
                <ConstantWidthStepContent>
                  {isResponseLoading && <ProgressContainerWithSpinner />}
                  {!isResponseLoading && response && (
                    <Fragment>
                      <Survey
                        response={response}
                        isValidCallback={handleValidityUpdate}
                      />
                      <WorkflowButtonGroup
                        handleBack={handleBack}
                        handleNext={handleNext}
                        disableNext={!isSurveyValid}
                      />
                    </Fragment>
                  )}
                </ConstantWidthStepContent>
              </Step>
              <Step>
                <StepButton
                  disabled={!isSurveyValid}
                  onClick={() => handleJump(dialogSections.length + 1)}
                >
                  <Typography variant="titleMedium">Next Steps</Typography>
                </StepButton>
                <ConstantWidthStepContent>
                  {isSummaryLoading && <ProgressContainerWithSpinner />}
                  {!isSummaryLoading && previewSummary && (
                    <HTMLWrapper innerHtml={previewSummary?.summary} />
                  )}
                  <WorkflowButtonGroup
                    handleBack={handleBack}
                    handleNext={handleCompleteWorkflowPhase}
                    nextLabel="Finish"
                  />
                </ConstantWidthStepContent>
              </Step>
            </Stepper>
          )}
        </DialogMainContent>
      </DialogContentContainer>
    </Dialog>
  );
};

export default WorkflowCompletionDialog;
